import { clearSession, sessionToken } from "./session-store.js";

/**
 * The one place a request leaves this app.
 *
 * The build is published to Cloudflare Pages and answered from its own origin.
 * `VITE_API_BASE` names that visible origin in production, where the Pages worker owns
 * `/api/*` and privately forwards it to the infrastructure API. That includes
 * `artwork_url`, which the API publishes as the
 * relative `/api/artwork/{id}` — the two places that render one put it through
 * `apiUrl` for exactly this reason. Unset, as in a dev run, the base is empty and
 * every path stays relative, which is what Vite's proxy expects.
 *
 * Every request carries the operator's bearer when there is one, because that is the
 * only credential the API reads (`apps/api/src/principal.ts:23`) and a public route
 * ignores it harmlessly. A 401 answered to a request that did carry one means the
 * bearer is dead, so it is retired here — once, in the one place requests are made,
 * rather than in every screen that might be the first to notice.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  /** The API's own machine-readable code, e.g. `invalid_crossfade`. */
  readonly code: string;
  /** The API's own explanation, when it gave one. */
  readonly detail: string;

  constructor(status: number, message: string, code = "", detail = "") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal | undefined;
}

/**
 * A request, and the whole of what a screen has to know about one: it either
 * resolves with the parsed body — `undefined` for the 204 the delete routes answer —
 * or throws an `ApiError` carrying what the API actually said.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = sessionToken();
  const headers: Record<string, string> = { accept: "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    throw new ApiError(0, "the API could not be reached");
  }

  if (response.status === 401 && token) clearSession();
  if (!response.ok) throw await apiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function apiGet<T>(
  path: string,
  init?: { signal?: AbortSignal | undefined },
): Promise<T> {
  return apiRequest<T>(path, { signal: init?.signal });
}

async function apiError(response: Response): Promise<ApiError> {
  let code = "";
  let detail = "";
  try {
    const body: unknown = await response.json();
    if (body !== null && typeof body === "object") {
      const shape = body as {
        error?: unknown;
        detail?: unknown;
        message?: unknown;
        issues?: unknown;
      };
      if (typeof shape.error === "string") code = shape.error;
      if (typeof shape.detail === "string") detail = shape.detail;
      else if (typeof shape.message === "string") detail = shape.message;
      else if (Array.isArray(shape.issues))
        detail = describeIssues(shape.issues);
    }
  } catch {
    // A non-JSON error body is not worth a second failure mode.
  }
  const message =
    [code, detail].filter((part) => part !== "").join(": ") ||
    `HTTP ${String(response.status)}`;
  return new ApiError(response.status, message, code, detail);
}

/** A rejected body names the fields it rejected; anything else is not an answer. */
function describeIssues(issues: unknown[]): string {
  const named = issues
    .map((issue) => {
      if (typeof issue !== "object" || issue === null) return null;
      const { path, message } = issue as { path?: unknown; message?: unknown };
      const field = Array.isArray(path) ? path.join(".") : "";
      const text = typeof message === "string" ? message : "";
      return [field, text].filter((part) => part !== "").join(" ") || null;
    })
    .filter((entry) => entry !== null);
  return named.join("; ");
}
